import { type ColumnDef, createColumnHelper, type SortingState } from "@tanstack/react-table";
import { parseISO } from "date-fns";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { type DataTableFeatures, RemoteDataTable } from "~/components/data-table";
import { type JobRun, LogsContainer } from "~/components/job-log";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui";

import { useDbpjkitWorkerJobRuns } from "~/features/dbpjkitWorkerJobs/hooks";
import type { JobRunStatus, RunRaw } from "~/features/dbpjkitWorkerJobs/types";
import { statusTone } from "~/features/dbpjkitWorkerJobs/utils/transformers";

const DEFAULT_PAGE_SIZE = 10;
const STATUS_OPTIONS: JobRunStatus[] = ["init", "in_progress", "succeeded", "failed", "canceled"];
const NO_SORTING: SortingState = [];

interface DbpjkitWorkerJobRunsHistoryProps {
  jobId: string;
  jobName: string;
}

const helper = createColumnHelper<DataTableFeatures, RunRaw>();

export function DbpjkitWorkerJobRunsHistory({ jobId, jobName }: DbpjkitWorkerJobRunsHistoryProps) {
  const { t } = useTranslation();
  const [statusFilter, setStatusFilter] = useState<JobRunStatus | "all">("all");
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [logRun, setLogRun] = useState<JobRun | null>(null);

  const { data } = useDbpjkitWorkerJobRuns({
    jobId,
    statusFilter,
    pageIndex,
    pageSize,
  });
  const rows = data.dbpjkitWorkerJobRuns;
  const totalCount = data.dbpjkitWorkerJobRunsAggregate.aggregate?.count ?? 0;

  const formatDate = (value?: string | null) => (value ? t("date.full", { date: parseISO(value) }) : "—");

  const columns: ColumnDef<DataTableFeatures, RunRaw, any>[] = [
    helper.accessor("statusId", {
      id: "statusId",
      header: () => t("model.dbpjkitWorkerJobRun.status"),
      enableSorting: false,
      cell: (info) => <Badge tone={statusTone(info.getValue())}>{info.getValue()}</Badge>,
    }),
    helper.accessor("queuedAt", {
      id: "queuedAt",
      header: () => t("model.dbpjkitWorkerJobRun.queuedAt"),
      enableSorting: false,
      cell: (info) => <span className="whitespace-nowrap tabular-nums text-fg-2">{formatDate(info.getValue())}</span>,
    }),
    helper.accessor("startedAt", {
      id: "startedAt",
      header: () => t("model.dbpjkitWorkerJobRun.startedAt"),
      enableSorting: false,
      cell: (info) => <span className="whitespace-nowrap tabular-nums text-fg-2">{formatDate(info.getValue())}</span>,
    }),
    helper.accessor("finishedAt", {
      id: "finishedAt",
      header: () => t("model.dbpjkitWorkerJobRun.finishedAt"),
      enableSorting: false,
      cell: (info) => <span className="whitespace-nowrap tabular-nums text-fg-2">{formatDate(info.getValue())}</span>,
    }),
    helper.display({
      id: "user",
      header: () => t("model.dbpjkitWorkerJobRun.user"),
      enableSorting: false,
      cell: (info) => {
        const user = info.row.original.user;
        if (!user) return <span className="text-fg-3">—</span>;
        return (
          <span className="flex flex-col">
            <span className="text-14-regular text-fg-1">{user.displayName ?? "—"}</span>
            <span className="text-12-regular text-fg-3">{user.email}</span>
          </span>
        );
      },
    }),
    helper.accessor("awsBatchJobId", {
      id: "awsBatchJobId",
      header: () => t("model.dbpjkitWorkerJobRun.awsBatchJobID"),
      enableSorting: false,
      cell: (info) => <span className="tabular-nums text-fg-2">{info.getValue() ?? "—"}</span>,
    }),
    helper.display({
      id: "logs",
      header: () => <span className="block text-right">{t("ui.logs")}</span>,
      enableSorting: false,
      cell: (info) => (
        <div className="flex justify-end">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              setLogRun({
                jobRunId: info.row.original.id,
                jobName,
                dryRun: false,
              })
            }
          >
            {t("ui.view")}
          </Button>
        </div>
      ),
    }),
  ];

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <h2 className="text-16-bold capitalize text-fg-1">{t("ui.history")}</h2>
        <Select
          value={statusFilter}
          onValueChange={(value) => {
            setStatusFilter(value as JobRunStatus | "all");
            setPageIndex(0);
          }}
        >
          <SelectTrigger className="md:w-60" aria-label={t("model.dbpjkitWorkerJobRun.status")}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("ui.allStatuses")}</SelectItem>
            {STATUS_OPTIONS.map((status) => (
              <SelectItem key={status} value={status}>
                {status}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <RemoteDataTable<RunRaw>
        columns={columns}
        data={rows}
        rowCount={totalCount}
        pageIndex={pageIndex}
        onPageIndexChange={setPageIndex}
        pageSize={pageSize}
        onPageSizeChange={(next) => {
          setPageSize(next);
          setPageIndex(0);
        }}
        sorting={NO_SORTING}
        onSortingChange={() => {}}
        emptyMessage={t("feature.common.noData")}
      />

      <Dialog open={logRun !== null} onOpenChange={(next) => !next && setLogRun(null)}>
        <DialogContent className="flex max-h-[90vh] flex-col sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>
              {t("ui.jobRunLogs")}
              {logRun && <span className="ml-2 text-12-regular text-fg-3">{logRun.jobRunId}</span>}
            </DialogTitle>
          </DialogHeader>
          {logRun && <LogsContainer jobRun={logRun} dryRun={logRun.dryRun} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
